const LODGR_EXTENSION_PING_EVENT = 'LODGR_EXT_PING'
const LODGR_EXTENSION_PONG_EVENT = 'LODGR_EXT_PONG'
const LODGR_EXT_SET_AUTH = 'LODGR_EXT_SET_AUTH'
export const LODGR_EXT_AUTH_PUSH = 'LODGR_EXT_AUTH_PUSH'
/** Extension auth went from present → cleared (popup logout); not used for empty REQUEST_AUTH. */
export const LODGR_EXT_AUTH_REVOKED = 'LODGR_EXT_AUTH_REVOKED'
const LODGR_EXT_CLEAR_AUTH = 'LODGR_EXT_CLEAR_AUTH'
const LODGR_EXT_REQUEST_AUTH = 'LODGR_EXT_REQUEST_AUTH'
const LODGR_EXT_SET_LINKING_INTENT = 'LODGR_EXT_SET_LINKING_INTENT'
// TENANT-5123: close the extension-opened opt-in tab and refocus its opener.
const LODGR_EXT_CLOSE_TAB = 'LODGR_EXT_CLOSE_TAB'
// TENANT-5011: ToS & Privacy acceptance bridge (mirrors the extension handshake).
const LODGR_EXT_REQUEST_LEGAL_STATUS = 'LODGR_EXT_REQUEST_LEGAL_STATUS'
const LODGR_EXT_LEGAL_STATUS = 'LODGR_EXT_LEGAL_STATUS'
const LODGR_EXT_LEGAL_ACCEPT = 'LODGR_EXT_LEGAL_ACCEPT'
const LODGR_EXT_LEGAL_ACCEPTED = 'LODGR_EXT_LEGAL_ACCEPTED'
// TENANT-5011: ensure-install bridge (mirrors the extension handshake).
const LODGR_EXT_ENSURE_INSTALL = 'LODGR_EXT_ENSURE_INSTALL'
const LODGR_EXT_INSTALL_ENSURED = 'LODGR_EXT_INSTALL_ENSURED'
const EXTENSION_INSTALLED_STORAGE_KEY = 'lodgrBrowserExtensionInstalled'
const EXTENSION_LINKING_PENDING_STORAGE_KEY = 'lodgrExternalProfileLinkingPending'

export const detectLodgrExtensionInstallation = async (timeoutMs = 500) => {
    if (typeof window === 'undefined' || typeof window.postMessage !== 'function') {
        return false
    }

    const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`

    return await new Promise((resolve) => {
        let timeoutId = null

        const cleanup = () => {
            if (timeoutId) {
                window.clearTimeout(timeoutId)
            }
            window.removeEventListener('message', onMessage)
        }

        const onMessage = (event) => {
            if (event.source !== window) return
            const messageData = event.data
            if (!messageData || messageData.type !== LODGR_EXTENSION_PONG_EVENT) return
            if (messageData.requestId !== requestId) return

            cleanup()
            resolve(true)
        }

        window.addEventListener('message', onMessage)
        window.postMessage({
            requestId,
            type: LODGR_EXTENSION_PING_EVENT,
        }, window.location.origin)

        timeoutId = window.setTimeout(() => {
            cleanup()
            resolve(false)
        }, timeoutMs)
    })
}

export const getStoredLodgrExtensionInstalledValue = () => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return false
    }

    return window.localStorage.getItem(EXTENSION_INSTALLED_STORAGE_KEY) === 'true'
}

export const markLodgrExtensionInstalled = () => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return
    }

    window.localStorage.setItem(EXTENSION_INSTALLED_STORAGE_KEY, 'true')
}

export const markExternalProfileLinkingPending = (provider = 'airbnb') => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return
    }

    window.localStorage.setItem(EXTENSION_LINKING_PENDING_STORAGE_KEY, provider)
}

export const consumeExternalProfileLinkingPending = () => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return null
    }

    const provider = window.localStorage.getItem(EXTENSION_LINKING_PENDING_STORAGE_KEY)
    window.localStorage.removeItem(EXTENSION_LINKING_PENDING_STORAGE_KEY)
    return provider
}

export const ensureLodgrExtensionInstallationDetected = async (timeoutMs = 500) => {
    try {
        const isInstalledViaHandshake = await detectLodgrExtensionInstallation(timeoutMs)
        if (isInstalledViaHandshake) {
            markLodgrExtensionInstalled()
        }
        return isInstalledViaHandshake
    } catch (error) {
        console.warn('ensureLodgrExtensionInstallationDetected - Error:', error)
        return false
    }
}

// Push the web session token into the extension so it can make authenticated API calls
export const sendAuthTokenToExtension = (token) => {
    if (typeof window === 'undefined' || !token) return
    window.postMessage({
        token, type: LODGR_EXT_SET_AUTH,
    }, window.location.origin)
}

// Clear the extension's stored auth token on logout
export const clearExtensionAuth = () => {
    if (typeof window === 'undefined') return
    window.postMessage({
        type: LODGR_EXT_CLEAR_AUTH,
    }, window.location.origin)
}

// TENANT-5123: ask the extension to close this tab and refocus the tab it was
// opened from (extension-entry opt-in flow). No-op when the extension isn't
// present — callers keep their own fallback navigation.
export const requestExtensionCloseTab = () => {
    if (typeof window === 'undefined' || typeof window.postMessage !== 'function') return
    window.postMessage({
        type: LODGR_EXT_CLOSE_TAB,
    }, window.location.origin)
}

// Push a linking intent into extension storage directly (replaces URL query-param transport)
export const sendLinkingIntentToExtension = (intent) => {
    if (typeof window === 'undefined' || !intent) return
    window.postMessage({
        intent, type: LODGR_EXT_SET_LINKING_INTENT,
    }, window.location.origin)
}

// TENANT-5011: ask the extension (via its handshake → background) to ensure a
// valid server-issued install exists, minting one if missing. The background is
// the single writer for install issuance and the call is idempotent. Resolves
// with the install data ({ installId, installToken }) or null if the extension
// can't answer (allow a longer timeout since minting hits the API).
export const ensureExtensionInstall = (timeoutMs = 3000) => {
    if (typeof window === 'undefined') return Promise.resolve(null)

    const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`

    return new Promise((resolve) => {
        let timeoutId = null

        const cleanup = () => {
            if (timeoutId) window.clearTimeout(timeoutId)
            window.removeEventListener('message', onMessage)
        }

        const onMessage = (event) => {
            if (event.source !== window) return
            const data = event.data
            if (!data || data.type !== LODGR_EXT_INSTALL_ENSURED) return
            if (data.requestId !== requestId) return
            cleanup()
            resolve(data.success ? (data.data || null) : null)
        }

        window.addEventListener('message', onMessage)
        window.postMessage({
            requestId,
            type: LODGR_EXT_ENSURE_INSTALL,
        }, window.location.origin)

        timeoutId = window.setTimeout(() => {
            cleanup()
            resolve(null)
        }, timeoutMs)
    })
}

// TENANT-5011: ask the extension (via its handshake → background) for the
// current ToS/Privacy acceptance status. Resolves with the status object
// ({ accepted, is_current, ... }) or null if the extension can't answer.
export const requestExtensionLegalStatus = (timeoutMs = 1500) => {
    if (typeof window === 'undefined') return Promise.resolve(null)

    const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`

    return new Promise((resolve) => {
        let timeoutId = null

        const cleanup = () => {
            if (timeoutId) window.clearTimeout(timeoutId)
            window.removeEventListener('message', onMessage)
        }

        const onMessage = (event) => {
            if (event.source !== window) return
            const data = event.data
            if (!data || data.type !== LODGR_EXT_LEGAL_STATUS) return
            if (data.requestId !== requestId) return
            cleanup()
            resolve(data.success ? (data.data || null) : null)
        }

        window.addEventListener('message', onMessage)
        window.postMessage({
            requestId,
            type: LODGR_EXT_REQUEST_LEGAL_STATUS,
        }, window.location.origin)

        timeoutId = window.setTimeout(() => {
            cleanup()
            resolve(null)
        }, timeoutMs)
    })
}

// TENANT-5011: record a ToS/Privacy acceptance through the extension (which
// owns the install token and records it server-side). Resolves with
// { success, data, error }.
export const recordExtensionLegalAcceptance = (timeoutMs = 8000) => {
    if (typeof window === 'undefined') {
        return Promise.resolve({
            error: 'No window', success: false,
        })
    }

    const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`

    return new Promise((resolve) => {
        let timeoutId = null

        const cleanup = () => {
            if (timeoutId) window.clearTimeout(timeoutId)
            window.removeEventListener('message', onMessage)
        }

        const onMessage = (event) => {
            if (event.source !== window) return
            const data = event.data
            if (!data || data.type !== LODGR_EXT_LEGAL_ACCEPTED) return
            if (data.requestId !== requestId) return
            cleanup()
            resolve({
                data: data.data || null,
                error: data.error,
                success: Boolean(data.success),
            })
        }

        window.addEventListener('message', onMessage)
        window.postMessage({
            requestId,
            type: LODGR_EXT_LEGAL_ACCEPT,
        }, window.location.origin)

        timeoutId = window.setTimeout(() => {
            cleanup()
            resolve({
                error: 'Timed out waiting for the extension', success: false,
            })
        }, timeoutMs)
    })
}

// Ask the extension for its current auth token; resolves with the token string or null
export const requestExtensionAuth = (timeoutMs = 500) => {
    if (typeof window === 'undefined') return Promise.resolve(null)

    return new Promise((resolve) => {
        let timeoutId = null

        const cleanup = () => {
            if (timeoutId) window.clearTimeout(timeoutId)
            window.removeEventListener('message', onMessage)
        }

        const onMessage = (event) => {
            if (event.source !== window) return
            if (!event.data || event.data.type !== LODGR_EXT_AUTH_PUSH) return
            cleanup()
            resolve(event.data.token || null)
        }

        window.addEventListener('message', onMessage)
        window.postMessage({
            type: LODGR_EXT_REQUEST_AUTH,
        }, window.location.origin)

        timeoutId = window.setTimeout(() => {
            cleanup()
            resolve(null)
        }, timeoutMs)
    })
}
